'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

const categories = [
  { id: 'cat-all', label: 'All Work' },
  { id: 'cat-web', label: 'Web Design' },
  { id: 'cat-ecom', label: 'E-Commerce' },
  { id: 'cat-branding', label: 'Branding' },
  { id: 'cat-mobile', label: 'Mobile' },
];

const projects = [
  {
    id: 'proj-lumina',
    slug: 'lumina-hotels',
    title: 'Lumina Hotels',
    category: 'web',
    type: 'Hotel Website',
    description: 'Luxury hotel chain with online booking, virtual tours, and multi-property management.',
    tech: ['Next.js', 'Stripe', 'Supabase'],
    color: 'from-amber/30 to-amber/5',
    accentColor: 'text-amber',
    size: 'large',
  },
  {
    id: 'proj-bloom',
    slug: 'bloom-beauty',
    title: 'Bloom Beauty',
    category: 'ecom',
    type: 'E-Commerce',
    description: 'D2C beauty brand with subscription boxes, loyalty program, and streamlined checkout.',
    tech: ['Shopify', 'React', 'Node.js'],
    color: 'from-rose/30 to-rose/5',
    accentColor: 'text-rose',
    size: 'small',
  },
  {
    id: 'proj-nexus',
    slug: 'nexus-finance',
    title: 'Nexus Finance',
    category: 'web',
    type: 'Fintech Platform',
    description: 'Investment analytics dashboard with real-time market data and portfolio management.',
    tech: ['React', 'TypeScript', 'WebSocket'],
    color: 'from-emerald/30 to-emerald/5',
    accentColor: 'text-emerald',
    size: 'small',
  },
  {
    id: 'proj-orion',
    slug: 'orion-academy',
    title: 'Orion Academy',
    category: 'web',
    type: 'EdTech Platform',
    description: 'K-12 learning management system with live classes, assignments, and progress tracking.',
    tech: ['Next.js', 'Supabase', 'TypeScript'],
    color: 'from-secondary/30 to-secondary/5',
    accentColor: 'text-secondary',
    size: 'small',
  },
  {
    id: 'proj-terra',
    slug: 'terra-real-estate',
    title: 'Terra Real Estate',
    category: 'web',
    type: 'Real Estate Portal',
    description: 'Property marketplace with advanced search, interactive map, and mortgage calculator.',
    tech: ['Next.js', 'Mapbox', 'Supabase'],
    color: 'from-accent/30 to-accent/5',
    accentColor: 'text-accent',
    size: 'large',
  },
  {
    id: 'proj-vitals',
    slug: 'vitals-health',
    title: 'Vitals Health',
    category: 'mobile',
    type: 'Healthcare App',
    description: 'Patient portal with appointment booking, telemedicine, and health record management.',
    tech: ['React Native', 'Expo', 'Supabase'],
    color: 'from-primary/30 to-primary/5',
    accentColor: 'text-primary',
    size: 'small',
  },
];

export default function PortfolioSection() {
  const [activeCategory, setActiveCategory] = useState('cat-all');

  const filtered = activeCategory === 'cat-all'
    ? projects
    : projects?.filter(p => `cat-${p?.category}` === activeCategory);

  return (
    <section id="portfolio" className="py-24 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="text-center mb-12">
          <span className="section-label">Our Work</span>
          <h2 className="font-display font-bold text-section-title text-foreground mt-3 mb-4">
            Projects That{' '}
            <span className="text-gradient-violet">Define Us</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base">
            A curated selection of our most impactful work across industries and disciplines.
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {categories?.map((cat) => (
            <button
              key={cat?.id}
              onClick={() => setActiveCategory(cat?.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold font-display transition-all duration-200 ${
                activeCategory === cat?.id
                  ? 'bg-primary text-white shadow-glow-primary'
                  : 'glass-card text-muted-foreground hover:text-foreground border border-border/40'
              }`}
            >
              {cat?.label}
            </button>
          ))}
        </div>

        {/* Portfolio grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
          {filtered?.map((project) => (
            <Link
              key={project?.id}
              href={`/projects/${project?.slug}`}
              className={`group glass-card rounded-2xl overflow-hidden border border-border/40 hover:border-primary/30 card-hover block ${
                project?.size === 'large' ? 'md:col-span-1 xl:col-span-1' : ''
              }`}
            >
              {/* Project visual */}
              <div className={`relative h-48 bg-gradient-to-br ${project?.color} flex items-center justify-center overflow-hidden`}>
                <div className="absolute inset-0 grid-dot-pattern opacity-30" />
                <div className="text-center z-10">
                  <p className="font-display font-bold text-2xl text-foreground/80">{project?.title}</p>
                  <span className={`text-xs font-mono-code ${project?.accentColor}`}>{project?.type}</span>
                </div>
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="p-2 glass-card rounded-lg border border-border/40">
                    <ArrowRight size={14} className="text-foreground" />
                  </div>
                </div>
              </div>

              {/* Project info */}
              <div className="p-5">
                <h3 className="font-display font-bold text-base text-foreground mb-2 group-hover:text-accent transition-colors">{project?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">{project?.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {project?.tech?.map((t) => (
                    <span key={`${project?.id}-tech-${t}`} className="text-[11px] font-mono-code px-2 py-0.5 bg-muted/60 rounded text-muted-foreground">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link href="/projects" className="btn-primary text-sm">
            View Full Portfolio <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}